import { i18n } from "@/locales/index.js";

import * as moment from 'moment';
import inscriptionService from "@/services/inscriptionService";
import haugazelService from "@/services/haugazelService";
import geoService from "@/services/geoService";
import streetService from "@/services/streetService";
import partnerHelpers from "@/helpers/partnerHelpers";

const createSubscription = async (context) => {
  try {
    if(!context.state.id) {
      let response = await inscriptionService.createSubscription();
      context.commit('setID', response.id);
      context.commit('setGUID', response.guid);
      context.commit('setSubscriptionDate', response.status.subscription_date);
      return {id: response.id, guid: response.guid};
    }
  } catch (error) {
    throw error;
  }
};

const createSubscriptionTestAchats = async (context, payload) => {
  try {
    if(!context.state.id) {
      let response = await inscriptionService.createSubscriptionTestAchats(payload);
      context.commit('setID', response.id);
      context.commit('setGUID', response.guid);
      context.commit('setSubscriptionDate', response.status.subscription_date);
      await context.dispatch('contract/setContract', response.contracts[0], { root: true });
      return {id: response.id, guid: response.guid};
    }
  } catch (error) {
    throw error;
  }
};

const createSubscriptionForAgent = async (context, payload) => {
  try {
    if(!context.state.id) {
      let response = await inscriptionService.createSubscriptionForAgent(
        payload.agent,
        payload.jwt,
        payload.data
      );
      context.commit('setID', response.id);
      context.commit('setGUID', response.guid);
      context.commit('setSubscriptionDate', response.status.subscription_date);
      await context.dispatch('contract/setContract', response.contracts[0], { root: true });
      return {id: response.id, guid: response.guid};
    } else {
      return {id: context.state.id, guid: context.state.guid};
    }
  } catch (error) {
    throw error;
  }
};

const setSubscription = async (context, sub) => {
  context.commit('setID', sub.id);
  context.commit('setGUID', sub.guid);
  context.commit('setSubscriptionDate', sub.status.subscription_date);
  if(sub.campaign) context.commit('setCampaign', sub.campaign);
  if(sub.promo) context.commit('setPromo', sub.promo);
  if(sub.language) {
    context.commit('setLanguage', sub.language);
    i18n.locale = sub.language;
  }

  if(sub.contact.civility) {
    let civilities = {
      MS: 'MO',
      DS: 'MA',
      ML: 'MA'
    }

    if(civilities[sub.contact.civility] !== undefined) {
      context.commit('setCivility', civilities[sub.contact.civility]);
      context.commit('setCoholderCivility', civilities[sub.contact.civility]);
    }
    else {
      context.commit('setCivility', sub.contact.civility);
    }
  }

  if(sub.contact.last_name) {
    let lastName = sub.contact.last_name.split(' - ');
    context.commit('setLastName', lastName[0]);

    if(lastName.length === 2)
      context.commit('setCoholderLastName', lastName[1]);
  }

  if(sub.contact.first_name) {
    let firstName = sub.contact.first_name.split(' - ');
    context.commit('setFirstName', firstName[0]);

    if(firstName.length === 2)
      context.commit('setCoholderFirstName', firstName[1]);
  }

  if(sub.contact.birth_date) context.commit('setBirthDate', moment(sub.contact.birth_date).format('DD/MM/YYYY'));
  if(sub.contact.email) context.commit('setMail', sub.contact.email);

  if(sub.contact.mobile && sub.contact.mobile.length || sub.contact.phone && sub.contact.phone.length) {
    let phoneValue = sub.contact.mobile.length ? sub.contact.mobile : sub.contact.phone;

    let mobile = phoneValue.match("^((\\+|00|)32\\s?|0|)4(5[56]|60|[6789]\\d)(\\s?\\d{2}){3}$");
    // let fix = phoneValue.match("^((\\+|00|)32\\s?|0|)(\\d(\s)?\\d{3}|\\d{2}(\s)?\\d{2})((\\s)?\\d{2}){2}$");
    let number = phoneValue.match("^(\\+32|0|\\+31|\\+33|\\+34|\\+352|\\+39|\\+41|\\+44|\\+48|\\+49)(\\d+)");
    if(number[1] === '0') number[1] = '+32';

    if(number) {
      if(mobile) {
        context.commit('setPhoneType', 'mobile');
      } else {
        context.commit('setPhoneType', 'phone');
      }
      context.commit('setPhoneNumber', number[2]);
      context.commit('setPhonePrefix', number[1]);
    }
    else {
      // If match nothing, no phone number
      context.commit('setPhoneNumber', null);
    }
  }

  if(sub.address.country) context.commit('setAddressCountry', sub.address.country);
  if(sub.address.street) context.commit('setAddressStreet', sub.address.street);
  if(sub.address.number) context.commit('setAddressNumber', sub.address.number);
  if(sub.address.box) context.commit('setAddressBox', sub.address.box);

  if(sub.address.post_code) {
    let locality = {
      zipCode: sub.address.post_code,
      town: sub.address.city,
    }

    if(!sub.address.country && sub.address.country === 'BE') {
      let geo = await geoService.getProviders(locality.zipCode);
      locality['region'] = geo.region;
      locality["ident"] = null;
      if (geo.region === 'wl') {
        let zones = await streetService.getWalloniaZone(locality.zipCode);
        let result = zones.candidates.find(e => e.city && e.city.ident);
        if (result) {
          locality["ident"] = result.city.ident;
        }
      }
    }

    context.commit('setAddressLocality', locality);
  }

  if(sub.can.set_sponsor !== null) context.commit('setShowSponsor', sub.can.set_sponsor);
  if(sub.can.set_temporary_contract !== null) context.commit('setShowTempContract', sub.can.set_temporary_contract);
  if(sub.can.send_remark !== null) context.commit('setSendRemark', sub.can.send_remark);
  if(sub.options.sponsor_customer_id) context.commit('setSponsorCustomerId', sub.options.sponsor_customer_id);
  context.commit('setCustomerType', !sub.is_residential ? 'pro' : 'resi');

  if(sub.company) {
    if(sub.company.vat_number) context.commit('setVatNumber', sub.company.vat_number);
    if(sub.company.name) context.commit('setCompanyName', sub.company.name);

    if(sub.company.legal_form) {
      let response = await haugazelService.getParameters();
      let legalForm = response.legalForms.find(legalForm => legalForm.id === sub.company.legal_form.toString());
      if(legalForm) context.commit('setLegalForm', legalForm.code);
    }
  }

  if(sub.payment.frequency) context.commit('setPaymentFrequency', sub.payment.frequency);
  if(sub.payment.mode) context.commit('setPaymentMode', sub.payment.mode);
  if(sub.payment.partial_dom) context.commit('setPartialDom', sub.payment.partial_dom);
  if(sub.payment.iban) context.commit('setIban', sub.payment.iban);
  if(sub.civility) context.commit('setHolderCivility', sub.civility);
  if(sub.payment.holder_first_name) context.commit('setHolderFirstName', sub.payment.holder_first_name);
  if(sub.payment.holder_last_name) context.commit('setHolderLastName', sub.payment.holder_last_name);
  if(sub.payment.agree_directdebit !== null) context.commit('setAgreeDirectDebit', parseInt(sub.payment.agree_directdebit));
  if(sub.payment.receive_instalment) context.commit('setReceiveInstalement', sub.payment.receive_instalment);
  if(sub.payment.instalment_day) context.commit('setInstalmentDayDD', sub.payment.instalment_day.toString());
  if(sub.payment.bic) context.commit('setBic', sub.payment.bic);
  if(sub.legal.accept_conditions) context.commit('setConditions', sub.legal.accept_conditions);
  if(sub.legal.accept_contact) context.commit('setOptinSales', sub.legal.accept_contact);
  if(sub.legal.accept_marketing) context.commit('setOptin', sub.legal.accept_marketing);
  if(sub.invoicing.remark) context.commit('setInvoiceRemark', sub.invoicing.remark);
  if(sub.invoicing.delivery_mode) context.commit('setInvoiceDeliveryMode', sub.invoicing.delivery_mode);
  if(sub.options.split_instalments_by_delivery) context.commit('setSplitInstalments', sub.options.split_instalments_by_delivery);
  if(sub.how_do_you_know) context.commit('setHowDoYouKnow', sub.how_do_you_know);
  if(sub.must.set_previous_providers !== null) context.commit('setProviderRequired', sub.must.set_previous_providers);
  if(sub.must.set_email !== null) context.commit('setMailRequired', sub.must.set_email);
  if(sub.origin) context.commit('setOrigin', sub.origin);
  if(sub.self_service !== null) context.commit('setSelfService', sub.self_service);
  if(sub.status.scenario) context.commit('setStatusScenario', sub.status.scenario);
  if(sub.status.signature_date) context.commit('setSignatureDate', moment(sub.status.signature_date).format('DD/MM/YYYY'));
  if(sub.status.subscription_date) context.commit('setSubscriptionDate', sub.status.subscription_date);
  if(sub.status.is_valid !== null) context.commit('setIsValid', sub.status.is_valid);
  if(sub.agent.id) {
    context.commit('setAgentCustomId', sub.agent.id);

    const partnerSettings = await partnerHelpers.getPartnerSettings(sub.agent.id); // ER-1212.
    context.commit("setAgentAllowBxlResi", partnerSettings.allow_bxl_resi);
  }
}

const setAddressContact = async (context, sub) => {
  // Address Contact
  if(sub.address) {
    const addressContact = {
      exist: 0,
      country: sub.address.country,
      street: sub.address.street,
      number: sub.address.number,
      box: sub.address.box,
      locality: {
        zipCode: sub.address.post_code,
        town: sub.address.city,
      }
    }
    context.commit('setAddressContact', addressContact);
  }
}

export default {
  createSubscription,
  createSubscriptionForAgent,
  createSubscriptionTestAchats,
  setSubscription,
  setAddressContact
};



// WEBPACK FOOTER //
// ./src/store/modules/subscription/actions.js